import { Cell, Avatar, Checkbox, Spinner } from '@/shared/ui/primitives';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Client } from '@/types';
import { TEST_ID_BUILDERS } from '@/testIds';
import { getInitials } from '@/shared/lib/client';
import { CreditBadge } from '@/shared/ui';
import { useLongPress } from '@/shared/hooks';
import { getPassDescription } from '../model/passDescription';
import styles from './ClientListItem.module.css';

interface ClientListItemProps {
  client: Client;
  onClick: (client: Client) => void;
  onLongPress: (client: Client) => boolean;
  isSelectionMode?: boolean;
  isExitingSelection?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  canMarkVisit?: boolean;
  isVisitPending?: boolean;
}

const INVALID_FEEDBACK_RESET_MS = 500;

export const ClientListItem: FC<ClientListItemProps> = ({
  client,
  onClick,
  onLongPress,
  isSelectionMode = false,
  isExitingSelection = false,
  isSelected = false,
  onToggleSelect,
  canMarkVisit = false,
  isVisitPending = false,
}) => {
  const [shifted, setShifted] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const invalidTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearInvalidTimer = useCallback(() => {
    if (invalidTimerRef.current) {
      clearTimeout(invalidTimerRef.current);
      invalidTimerRef.current = null;
    }
  }, []);

  const triggerInvalidFeedback = useCallback(() => {
    clearInvalidTimer();
    setIsInvalid(true);
    navigator.vibrate?.(60);
    invalidTimerRef.current = setTimeout(() => {
      setIsInvalid(false);
      invalidTimerRef.current = null;
    }, INVALID_FEEDBACK_RESET_MS);
  }, [clearInvalidTimer]);

  const handleSelectToggle = useCallback(() => {
    if (isVisitPending) return;
    if (!canMarkVisit) {
      triggerInvalidFeedback();
      return;
    }
    onToggleSelect?.(client.id);
  }, [canMarkVisit, isVisitPending, client.id, onToggleSelect, triggerInvalidFeedback]);

  const handleLongPress = useCallback(() => {
    if (isVisitPending) return;
    const wasSelected = onLongPress(client);
    if (wasSelected) {
      setShifted(true);
    } else {
      triggerInvalidFeedback();
    }
  }, [client, isVisitPending, onLongPress, triggerInvalidFeedback]);

  const {
    handlers: pressHandlers,
    wasLongPress,
    resetLongPress,
  } = useLongPress({
    onLongPress: handleLongPress,
  });

  useEffect(() => clearInvalidTimer, [clearInvalidTimer]);

  useEffect(() => {
    if (!isSelectionMode) {
      setShifted(false);
      setIsInvalid(false);
      clearInvalidTimer();
    }
  }, [clearInvalidTimer, isSelectionMode]);

  const activeShift = (shifted || isSelectionMode) && !isExitingSelection;
  let wrapperClassName = styles.cellWrapper;
  if (isInvalid) {
    wrapperClassName += ` ${styles.cellWrapperInvalid}`;
  }
  if (activeShift) {
    wrapperClassName += ` ${styles.cellWrapperShifted}`;
  }

  const passDescription = getPassDescription(client);

  return (
    <div className={styles.root} data-testid={TEST_ID_BUILDERS.clientItem(client.name)}>
      <div
        className={styles.checkbox}
        aria-hidden={!activeShift}
        style={{
          flex: `0 0 ${activeShift ? '44px' : '0'}`,
          marginRight: activeShift ? '8px' : '0',
          transition: 'flex-basis 0.25s ease, margin-right 0.25s ease',
        }}
      >
        <Checkbox
          checked={isSelected}
          aria-label={`Select ${client.name}`}
          tabIndex={activeShift ? 0 : -1}
          disabled={isVisitPending || !canMarkVisit || !activeShift}
          onChange={handleSelectToggle}
          style={{
            opacity: activeShift ? 1 : 0,
            transform: activeShift ? 'scale(1)' : 'scale(0.5)',
            transition: 'opacity 0.25s ease, transform 0.25s ease',
          }}
        />
      </div>

      <div
        className={wrapperClassName}
        style={{
          flex: '1 1 auto',
          opacity: !canMarkVisit && isSelectionMode ? 0.6 : 1,
          transition: 'opacity 0.25s ease',
        }}
      >
        <Cell
          disabled={isVisitPending}
          aria-busy={isVisitPending}
          aria-keyshortcuts="Shift+Space"
          leading={
            isSelectionMode ? null : <Avatar initials={getInitials(client.name)} size={48} />
          }
          trailing={
            <>
              {client.pass && <CreditBadge pass={client.pass} />}
              {isVisitPending && <Spinner size="sm" />}
            </>
          }
          subtitle={
            isVisitPending ? (
              'Recording visit…'
            ) : (
              <span style={{ color: passDescription.color }}>{passDescription.text}</span>
            )
          }
          onClick={() => {
            if (wasLongPress()) {
              resetLongPress();
              return;
            }
            if (isSelectionMode) {
              handleSelectToggle();
            } else {
              onClick(client);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === ' ' && event.shiftKey && !event.repeat) {
              event.preventDefault();
              if (isSelectionMode) handleSelectToggle();
              else handleLongPress();
            }
          }}
          {...pressHandlers}
        >
          {client.name}
        </Cell>
      </div>
    </div>
  );
};
